import React, { useState } from 'react';
import { getApiUrl } from '../config';
import styles from './Cart.module.css';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
  size?: string;
}

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onCheckout?: () => void;
}

const Cart: React.FC<CartProps> = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
}) => {
  const [email, setEmail] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [emailError, setEmailError] = useState('');

  const total = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCheckout = async () => {
    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsCheckingOut(true);
    setEmailError('');

    try {
      console.log('🔍 Starting checkout process...');
      console.log('🔍 API URL:', getApiUrl('/api/create-checkout-session'));
      console.log('🔍 Request payload:', { items, email });

      const response = await fetch(getApiUrl('/api/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          email: email,
        }),
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Error response:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();
      console.log('🔍 Response data:', responseData);

      const { sessionId } = responseData;

      if (!sessionId) {
        throw new Error('No session ID received from server');
      }

      // Redirect to Stripe Checkout
      const stripe = await import('@stripe/stripe-js').then(module =>
        module.loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '')
      );

      if (stripe) {
        console.log('🔍 Redirecting to Stripe with session ID:', sessionId);
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.error('🔍 Stripe redirect error:', error);
          throw new Error(`Stripe error: ${error.message}`);
        }
      } else {
        throw new Error('Failed to load Stripe');
      }
    } catch (error) {
      console.error('🔍 Checkout error:', error);
      setEmailError(
        `Checkout failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className={styles.cart}>
        <h2 className={styles.cartTitle}>Your Cart</h2>
        <p className={styles.emptyCart}>Your cart is empty</p>
      </div>
    );
  }

  return (
    <div className={styles.cart}>
      <h2 className={styles.cartTitle}>Your Cart</h2>

      <div className={styles.cartContent}>
        {/* Cart Items */}
        <div className={styles.cartItems}>
          {items.map(item => (
            <div key={item.id} className={styles.cartItem}>
              <img
                src={item.imageUrl}
                alt={item.name}
                className={styles.cartItemImage}
              />
              <div className={styles.cartItemContent}>
                <h3 className={styles.cartItemName}>{item.name}</h3>
                <p className={styles.cartItemPrice}>${item.price.toFixed(2)}</p>
                {item.size && (
                  <p className={styles.cartItemSize}>Size: {item.size}</p>
                )}
                <div className={styles.quantityControls}>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                    className={styles.quantityButton}
                  >
                    -
                  </button>
                  <span className={styles.quantityDisplay}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    className={styles.quantityButton}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className={styles.cartSummary}>
          <h3 className={styles.cartTotalTitle}>Order Summary</h3>
          <div className={styles.totalBreakdown}>
            <div className={styles.totalRow}>
              <span>Subtotal:</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Estimated Tax (8.25%):</span>
              <span>${(total * 0.0825).toFixed(2)}</span>
            </div>
            <div className={styles.totalRowTotal}>
              <span>Total:</span>
              <span>${(total * 1.0825).toFixed(2)}</span>
            </div>
          </div>
          <p className={styles.checkoutNote}>
            * Final shipping cost, tax, and total will be calculated during
            checkout
          </p>
        </div>

        {/* Email Collection */}
        <div className={styles.emailSection}>
          <label htmlFor="checkout-email" className={styles.emailLabel}>
            Email Address *
          </label>
          <input
            id="checkout-email"
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            placeholder="Enter your email address"
            className={`${styles.emailInput} ${emailError ? styles.error : ''}`}
            required
          />
          {emailError && <p className={styles.emailError}>{emailError}</p>}
          <p className={styles.emailHelp}>
            We&apos;ll send your order confirmation to this email address.
          </p>
        </div>

        <button
          onClick={handleCheckout}
          disabled={isCheckingOut || !email.trim()}
          className={styles.checkoutButton}
        >
          {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
        </button>
      </div>
    </div>
  );
};

export default Cart;
